import type { FetchPolicy } from "@apollo/client";
import { autorun, makeAutoObservable, reaction, runInAction } from "mobx";
import { CONNECTION } from "utils/config";
import { http } from "utils/http";
import { SLACK_WEBHOOKS } from "../utils/slack";
import { VISIBILITY_STATE } from "./constants";
import { notificationStore } from "./notificationStore";

export enum APP_STATE {
  INIT = "init",
  LOADING = "loading",
  READY = "ready",
  ERROR = "error",
}

export class CommonStore {
  connectionStatus: (typeof CONNECTION)[keyof typeof CONNECTION] =
    CONNECTION.ONLINE;
  /**
   * The visibility of the current tab.
   * Based on visibilityChange event.
   * See `setVisibilityState()`.
   */
  visibilityState: DocumentVisibilityState = VISIBILITY_STATE.VISIBLE;
  appState = APP_STATE.INIT;
  isDataAvailable = true;
  /** Rendered height (px) of the bottom-docked SQL editor; 0 when hidden or docked right. */
  sqlWidgetHeight = 0;

  fetchOptions = {};

  constructor() {
    makeAutoObservable(this);

    autorun(() => {
      if (
        this.connectionStatus === CONNECTION.OFFLINE ||
        this.visibilityState === VISIBILITY_STATE.HIDDEN
      ) {
        this.setFetchOptions({
          fetchPolicy: "cache-only",
        });
      } else if (this.visibilityState === VISIBILITY_STATE.VISIBLE) {
        this.setFetchOptions({});
      }
    });

    reaction(
      () => this.isDataAvailable,
      (isAvailable) => {
        if (!isAvailable) {
          notificationStore.setPersistentBanner("OFFLINE");
        } else if (notificationStore.activePersistentBanner === "OFFLINE") {
          notificationStore.clearPersistentBanner();
        }
      },
    );
  }

  setFetchOptions = (option: { fetchPolicy?: FetchPolicy }) => {
    runInAction(() => {
      this.fetchOptions = option;
    });
  };

  setConnectionStatus(status: (typeof CONNECTION)[keyof typeof CONNECTION]) {
    this.connectionStatus = status;

    if (this.connectionStatus === CONNECTION.OFFLINE) {
      notificationStore.setPersistentBanner("OFFLINE");
    } else if (notificationStore.activePersistentBanner === "OFFLINE") {
      notificationStore.clearPersistentBanner();
    }
  }

  /**
   * Plug this into an event listener to listen for visibility change events.
   *
   * "This event fires with a visibilityState of hidden when
   * a user navigates to a new page, switches tabs, closes the tab,
   * minimizes or closes the browser, or, on mobile, switches
   * from the browser to a different app."
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event
   */
  setVisibilityState = (state: DocumentVisibilityState) => {
    runInAction(() => {
      this.visibilityState = state as VISIBILITY_STATE;
    });
  };

  setIsDataAvailable = (status: boolean) => {
    runInAction(() => {
      this.isDataAvailable = status;
    });
  };

  setSqlWidgetHeight = (height: number) => {
    runInAction(() => {
      this.sqlWidgetHeight = height;
    });
  };

  setAppState = (state: APP_STATE) => {
    runInAction(() => {
      this.appState = state;
    });
  };

  // The default Slack webhook is to #feed-feature-requests
  postSlackWebhook = async (
    data: string,
    toast: boolean,
    webHookUrl: string = SLACK_WEBHOOKS.FEED_FEATURE_REQUESTS,
  ) => {
    try {
      // Slack rejects CORS preflights — must not set Content-Type, so use raw body, not json:.
      await http.fetch(webHookUrl, {
        method: "POST",
        body: data,
        keepalive: true,
      });
      if (toast)
        notificationStore.showSuccessToaster(
          "Your feedback has been successfully submitted!",
        );
      return true;
    } catch {
      notificationStore.showErrorToaster(
        "Something went wrong, and we are unable to process your request. Please, try again later.",
      );
      return false;
    }
  };
}

export const commonStore = new CommonStore();
